import { flexRender, type Row } from '@tanstack/react-table';

import { TableCell, TableRow } from '@mui/material';
import { cn } from '../utils';
import { useDataTableContext } from './data-table-context';

type DataTableRowProps<T> = {
  row: Row<T>;
};

export const DataTableRow = <T,>({ row, ...props }: DataTableRowProps<T>) => {
  const { onRowClick, getCommonPinningStyles, focusedCellId } =
    useDataTableContext();

  return (
    <TableRow
      key={row.id}
      onClick={() => {
        onRowClick?.(row.original);
      }}
      className={cn('group/row', {
        'hover:bg-white cursor-pointer': !!onRowClick,
      })}
      {...props}
    >
      {row.getVisibleCells().map((cell) => (
        <TableCell
          className={cn(
            'pl-6 whitespace-nowrap overflow-hidden overflow-ellipsis py-3 bg-white group-hover/row:bg-[#D9DBDE]',
            // Styles for bottom border using an :after pseudo-element
            'relative after:absolute after:bottom-px after:left-0 after:w-full after:h-px group-last/row:after:h-0 after:bg-light-200 after:translate-y-px',
            focusedCellId === `${cell.id}` && 'border border-green-600',
          )}
          key={cell.id}
          style={{
            width: cell.column.getSize(),
            ...getCommonPinningStyles(cell.column),
          }}
        >
          {/* Wrap in an element so iOS in-browser translation can't detach a
              bare text node out from under React's removeChild (SC-3386).
              `contents` keeps it layout-neutral. */}
          <span className="contents">
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </span>
        </TableCell>
      ))}
    </TableRow>
  );
};
